<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import type { IWarehouse } from '@nova/core';

import AppointmentLoadTypeDetails from '@/components/AppointmentLoadTypeDetails.vue';
import { routeToAppointmentErrorScreen } from '@/composables/useApiErrorRouting';
import { useAppointmentLookup } from '@/composables/useAppointmentLookup';
import { CheckinFlowStep, useCheckinFlow } from '@/composables/useCheckinFlow';
import { inferAssetVisitTypeFromLoadType } from '@/utils/assetVisitType';

defineOptions({ name: 'WelcomePage' });

const DEFAULT_REFERENCE_NUMBER_DISPLAY_NAME = 'reference number';

const props = defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const checkinFlow = useCheckinFlow();

const warehouseId = computed(() => (route.query.warehouseId as string) ?? '');

const { identifier, isLookingUp, appointment, notFoundError, search } = useAppointmentLookup({
  warehouseId,
  onApiError: error => routeToAppointmentErrorScreen(router, warehouseId.value, error)
});

const referenceNumberInputMode = computed(() => props.warehouse.settings?.referenceNumberInputMode);

// Warehouses can rename the reference number ("PO Number", "Shipment ID", …). The lookup also
// accepts a confirmation number, so the custom name is folded into the same suffix the legacy
// form uses instead of replacing the whole label.
const referenceNumberLabel = computed(() => {
  const displayName = String(props.warehouse.settings?.referenceNumberDisplayName ?? '').trim();
  return displayName && displayName.toLowerCase() !== DEFAULT_REFERENCE_NUMBER_DISPLAY_NAME
    ? t('appointmentForm.identifierSuffix', { reference: displayName })
    : t('initial.referenceNumberLabel');
});

// The unplanned flow never collects a reference, so offering it would strand the driver on the
// details step, where the requirement is enforced but no field exists to satisfy it.
const showNoReferenceOption = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceRequired !== true
);

const mustMatchAppointment = computed(
  () => props.warehouse.settings?.gateManagementDriverReferenceValidated === true
);

// When the reference must match an appointment there is no fallback path, so a value the backend
// already rejected can only be retried by editing it — which clears notFoundError.
const isCheckInDisabled = computed(
  () =>
    !identifier.value.trim() ||
    isLookingUp.value ||
    (mustMatchAppointment.value && notFoundError.value)
);

async function handleCheckIn() {
  if (!appointment.value) {
    await search();
  }

  if (appointment.value) {
    checkinFlow.setAppointmentId(appointment.value.id);
    checkinFlow.setLoadTypeId(appointment.value.loadType?.id ?? null);
    checkinFlow.setAssetVisitType(inferAssetVisitTypeFromLoadType(appointment.value.loadType));
    checkinFlow.setIdentifier(identifier.value);
    checkinFlow.setAppointment(appointment.value);

    checkinFlow.setNextStep(CheckinFlowStep.CheckinDetailsPage);
    return;
  }

  if (!mustMatchAppointment.value) {
    startUnplannedCheckin(identifier.value);
  }
}

function startUnplannedCheckin(referenceNumber: string) {
  checkinFlow.setAppointmentId(null);
  checkinFlow.setAppointment(null);
  checkinFlow.setIdentifier(referenceNumber);
  checkinFlow.setNextStep(CheckinFlowStep.PurposeOfStop);
}

function handleNoReference() {
  startUnplannedCheckin(identifier.value);
}
</script>

<template>
  <div class="comet-i18n-exclude flex-grow-1 d-flex flex-column justify-space-between">
    <div class="d-flex flex-column gap-4">
      <p class="typography-heading-sm-bold">{{ t('initial.lookupTitle') }}</p>

      <v-text-field
        v-model="identifier"
        :label="referenceNumberLabel"
        :type="referenceNumberInputMode === 'numeric' ? 'number' : 'text'"
        :inputmode="referenceNumberInputMode === 'numeric' ? 'numeric' : 'text'"
        color="primary"
        variant="outlined"
        class="reference-number-field"
        data-testid="comet-reference-lookup"
        :error-messages="notFoundError ? t('initial.referenceNotFoundError') : ''"
        hide-details="auto">
        <template #append-inner>
          <v-progress-circular
            v-if="isLookingUp"
            indeterminate
            size="20"
            width="2"
            color="primary"></v-progress-circular>
        </template>
      </v-text-field>

      <appointment-load-type-details
        v-if="appointment"
        :appointment="appointment"
        variant="highlighted" />

      <v-btn
        block
        append-icon="mdi-arrow-right"
        color="primary"
        :disabled="isCheckInDisabled"
        data-testid="comet-check-in"
        @click="handleCheckIn">
        {{ t('initial.checkIn') }}
      </v-btn>
    </div>

    <div v-if="showNoReferenceOption" class="d-flex flex-column gap-4">
      <div class="d-flex align-center gap-2">
        <v-divider></v-divider>
        <span class="typography-body-sm">{{ t('initial.orDivider') }}</span>
        <v-divider></v-divider>
      </div>

      <v-card
        variant="outlined"
        role="button"
        tabindex="0"
        class="no-reference-card pa-4 d-flex flex-column gap-4 bg--color-neutral-10 border--color-neutral-40"
        data-testid="comet-no-reference"
        @click="handleNoReference"
        @keydown.enter="handleNoReference"
        @keydown.space.prevent="handleNoReference">
        <div class="d-flex flex-column gap-1 text-left">
          <span class="typography-body-md-bold">{{ t('initial.noReferenceTitle') }}</span>
          <span class="typography-body-sm">{{ t('initial.noReferenceDescription') }}</span>
        </div>

        <v-btn
          block
          color="secondary"
          class="typography-button-md"
          append-icon="mdi-arrow-right"
          data-testid="comet-no-reference-cta"
          @click.stop="handleNoReference">
          {{ t('initial.checkInWithoutAppointment') }}
        </v-btn>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
.reference-number-field :deep(.v-input__details) {
  padding-left: 0;
}

.no-reference-card {
  border-radius: 8px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $color-text-link;
    outline-offset: 2px;
  }
}
</style>
